import { createStore } from 'zustand';
import { SavedStackItem, StackItem } from '../../../../interfaces/use-order-item-stack.types';
import {
  CreateGridStoreDeps,
  GridCells,
  GridStoreState,
  ReconcileParams,
  SeedGridParams,
  SetCellQuantityParams,
} from './gridStore.types';
import {
  buildCombinationKey,
  deduplicateStackByCell,
  mergeSeedIntoCells,
  reconcileState,
} from './gridStore.utils';

type AfterSaveHandler = (savedItems: SavedStackItem[]) => void;

interface PendingCellTarget {
  combinationKey: string;
  skuId: string;
  quantity: number;
}

export const createGridStore = (deps: CreateGridStoreDeps) => {
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let flushPending: (() => Promise<void>) | undefined;
  let isSaving = false;
  let hasPendingSaveRequest = false;
  let persistStackItems = deps.persistStackItems;
  const afterSaveHandlers = new Map<string, AfterSaveHandler>();

  const store = createStore<GridStoreState>((set, get) => {
    const collectPendingCells = (cells: GridCells) => {
      const targets: PendingCellTarget[] = [];
      Object.entries(cells).forEach(([combinationKey, combination]) => {
        Object.entries(combination).forEach(([skuId, cell]) => {
          if (cell.isPending) {
            targets.push({ combinationKey, skuId, quantity: cell.quantity });
          }
        });
      });
      return targets;
    };

    const rollbackFailedCells = (targets: PendingCellTarget[]) => {
      set((state) => {
        let cells = state.cells;
        let changed = false;
        targets.forEach(({ combinationKey, skuId, quantity }) => {
          const cell = cells[combinationKey]?.[skuId];
          if (!cell?.isPending || cell.quantity !== quantity) {
            return;
          }
          if (!changed) {
            cells = { ...cells };
            changed = true;
          }
          cells[combinationKey] = {
            ...cells[combinationKey],
            [skuId]: {
              quantity: cell.rollbackQuantity ?? 0,
              isPending: false,
              orderItemId: cell.orderItemId,
            },
          };
        });
        return changed ? { cells } : state;
      });
    };

    const saveStack = async () => {
      if (isSaving) {
        hasPendingSaveRequest = true;
        return;
      }

      const snapshot = deduplicateStackByCell(get().pending);
      if (snapshot.length === 0) {
        return;
      }

      const inFlightCells = collectPendingCells(get().cells);

      isSaving = true;
      try {
        await persistStackItems(snapshot);
        set((state) => ({ pending: state.pending.filter((item) => !snapshot.includes(item)) }));
        rollbackFailedCells(inFlightCells);
      } finally {
        isSaving = false;
      }

      if (hasPendingSaveRequest) {
        hasPendingSaveRequest = false;
        await saveStack();
      }
    };
    flushPending = saveStack;

    const setCellQuantity = ({ combinationKey, skuId, quantity }: SetCellQuantityParams) => {
      set((state) => {
        const prevCombination = state.cells[combinationKey] ?? {};
        const prevCell = prevCombination[skuId];
        const rollbackQuantity = prevCell?.isPending
          ? prevCell.rollbackQuantity
          : (prevCell?.quantity ?? 0);
        const nextCombination = {
          ...prevCombination,
          [skuId]: { ...prevCell, quantity, isPending: true, rollbackQuantity },
        };
        return {
          cells: {
            ...state.cells,
            [combinationKey]: nextCombination,
          },
        };
      });
    };

    const stackItem = (item: StackItem) => {
      set((state) => ({
        pending: deduplicateStackByCell([item, ...state.pending]),
      }));

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(saveStack, deps.debounceMs ?? 1000);
    };

    const reconcile = ({ combinationKey, savedItems }: ReconcileParams) => {
      set((state) => reconcileState({ state, combinationKey, savedItems }) ?? state);
    };

    const seed = ({ combinationKey, grid }: SeedGridParams) => {
      set((state) => {
        const pendingSkuIds = new Set(
          state.pending
            .filter((item) => buildCombinationKey(item) === combinationKey)
            .map((item) => item.skuId)
            .filter((skuId): skuId is string => Boolean(skuId)),
        );

        const cells = mergeSeedIntoCells({
          cells: state.cells,
          combinationKey,
          grid,
          pendingSkuIds,
        });

        if (cells === state.cells) {
          return state;
        }

        return { cells };
      });
    };

    const clearCombination = (combinationKey: string) => {
      set((state) => {
        const current = state.cells[combinationKey] ?? {};
        const cleared: GridCells[string] = {};
        Object.keys(current).forEach((skuId) => {
          cleared[skuId] = { quantity: 0, isPending: false };
        });

        return {
          cells: { ...state.cells, [combinationKey]: cleared },
          pending: state.pending.filter((item) => buildCombinationKey(item) !== combinationKey),
        };
      });
    };

    return {
      cells: {},
      pending: [],
      setCellQuantity,
      stackItem,
      reconcile,
      seed,
      clearCombination,
    };
  });

  const flushAndCancel = () => {
    clearTimeout(debounceTimer);
    flushPending?.();
  };

  const setPersistStackItems = (fn: CreateGridStoreDeps['persistStackItems']) => {
    persistStackItems = fn;
  };

  const registerAfterSave = (combinationKey: string, handler: AfterSaveHandler) => {
    afterSaveHandlers.set(combinationKey, handler);
    return () => {
      if (afterSaveHandlers.get(combinationKey) === handler) {
        afterSaveHandlers.delete(combinationKey);
      }
    };
  };

  const notifyCombinationSaved = (combinationKey: string, savedItems: SavedStackItem[]) => {
    afterSaveHandlers.get(combinationKey)?.(savedItems);
  };

  return Object.assign(store, {
    flushAndCancel,
    registerAfterSave,
    notifyCombinationSaved,
    setPersistStackItems,
  });
};

export type GridStoreApi = ReturnType<typeof createGridStore>;

export const emptyGridStore = createGridStore({ persistStackItems: async () => undefined });
